import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Settings, Users, Smartphone, CheckCircle, XCircle, AlertTriangle, RefreshCw, Send, ArrowDownLeft, ArrowUpRight, X, Bot, UserCheck, Loader2, Search, VolumeX, Unlink, ChevronDown, ChevronUp, ExternalLink, FileText } from '../../icons/wp-icons';
import toast from 'react-hot-toast';
import { whatsappApi } from '../../api/whatsappApi';
import { useEmbed } from '../../hooks/useEmbed';
import { censorName, censorBody, censorPhone } from '../../utils/demoCensor';

const SUB_TABS = [
  { id: 'status', label: 'Status', icon: MessageCircle },
  { id: 'einstellungen', label: 'Einstellungen', icon: Settings },
  { id: 'konversationen', label: 'Konversationen', icon: Users },
  { id: 'business', label: 'Business App', icon: Smartphone },
  { id: 'antwortsperre', label: 'Antwort-Sperre', icon: VolumeX },
];

export default function WhatsAppAgent({ readOnly }) {
  const navigate = useNavigate();
  const { isEmbed } = useEmbed();
  const [activeTab, setActiveTab] = useState('status');
  const [settings, setSettings] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [convPage, setConvPage] = useState(1);
  const [convTotal, setConvTotal] = useState(0);
  const [selectedConv, setSelectedConv] = useState(null);
  const [selectedConvDetail, setSelectedConvDetail] = useState(null);
  const [selectedConvMessages, setSelectedConvMessages] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [manualMessage, setManualMessage] = useState('');
  const [connectForm, setConnectForm] = useState({ phoneNumberId: '', businessAccountId: '', phoneNumber: '' });
  const [disconnecting, setDisconnecting] = useState(false);
  const [convSearch, setConvSearch] = useState('');
  const [convSearchInput, setConvSearchInput] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-Antwort-Sperre
  const [waSperren, setWaSperren] = useState([]);
  const [newWaSperrePhone, setNewWaSperrePhone] = useState('');

  useEffect(() => {
    loadSettings();
    loadWaSperren();
  }, []);

  useEffect(() => {
    if (activeTab === 'konversationen') loadConversations();
    if (activeTab === 'antwortsperre') loadWaSperren();
  }, [activeTab, convPage, convSearch]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConvMessages]);

  const loadSettings = async () => {
    try {
      const data = await whatsappApi.getSettings();
      setSettings(data);
    } catch (err) {
      console.error('WhatsApp Settings laden fehlgeschlagen:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadWaSperren = async () => {
    try {
      const data = await whatsappApi.getAutoAntwortSperren();
      setWaSperren(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('WA Antwort-Sperren laden fehlgeschlagen:', err);
    }
  };

  const handleToggleWaSperre = async (phone) => {
    const existing = waSperren.find(s => s.kontakt === phone);
    try {
      if (existing) {
        await whatsappApi.removeAutoAntwortSperre(phone);
        toast.success('Auto-Antwort wieder aktiviert');
      } else {
        await whatsappApi.addAutoAntwortSperre(phone);
        toast.success('Auto-Antwort gesperrt');
      }
      loadWaSperren();
    } catch (err) {
      toast.error('Fehler');
    }
  };

  const loadConversations = async () => {
    try {
      const params = { page: convPage, limit: 20 };
      if (convSearch) params.search = convSearch;
      const data = await whatsappApi.getConversations(params);
      setConversations(data.conversations || data.items || []);
      setConvTotal(data.total || data.pagination?.total || 0);
    } catch (err) {
      console.error('Konversationen laden fehlgeschlagen:', err);
    }
  };

  const handleConvSearch = (e) => {
    e.preventDefault();
    setConvPage(1);
    setSelectedConv(null);
    setSelectedConvDetail(null);
    setSelectedConvMessages([]);
    setConvSearch(convSearchInput);
  };

  const loadConversationDetail = async (conv) => {
    if (selectedConv?.id === conv.id) {
      setSelectedConv(null);
      setSelectedConvDetail(null);
      setSelectedConvMessages([]);
      setManualMessage('');
      return;
    }
    setSelectedConv(conv);
    setConvLoading(true);
    try {
      const data = await whatsappApi.getConversation(conv.id);
      setSelectedConvDetail(data);
      setSelectedConvMessages(data.nachrichten || data.messages || []);
    } catch (err) {
      console.error('Konversation laden fehlgeschlagen:', err);
      setSelectedConvDetail(null);
      setSelectedConvMessages([]);
    } finally {
      setConvLoading(false);
    }
  };

  const reloadSelectedConv = async () => {
    if (!selectedConv) return;
    try {
      const data = await whatsappApi.getConversation(selectedConv.id);
      setSelectedConvDetail(data);
      setSelectedConvMessages(data.nachrichten || data.messages || []);
    } catch (err) {
      console.error('Konversation neu laden fehlgeschlagen:', err);
    }
  };

  const handleSendMessage = async () => {
    if (!manualMessage.trim() || !selectedConv || sending || readOnly) return;
    setSending(true);
    try {
      await whatsappApi.sendMessage(selectedConv.id, manualMessage.trim());
      setManualMessage('');
      await reloadSelectedConv();
      toast.success('Nachricht gesendet');
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Nachricht konnte nicht gesendet werden';
      if (err.response?.data?.code === 'SESSION_EXPIRED') {
        toast.error('Das 24h-Fenster ist abgelaufen. Der Kunde muss zuerst eine Nachricht senden.');
      } else {
        toast.error(errMsg);
      }
    } finally {
      setSending(false);
    }
  };

  const handleHandoff = async () => {
    if (!selectedConv) return;
    try {
      await whatsappApi.handoff(selectedConv.id, 120);
      await reloadSelectedConv();
      toast.success('KI pausiert — Sie können jetzt manuell antworten');
    } catch (err) {
      toast.error('Fehler beim Übergeben');
    }
  };

  const handleResume = async () => {
    if (!selectedConv) return;
    try {
      await whatsappApi.resume(selectedConv.id);
      await reloadSelectedConv();
      toast.success('KI-Agent wieder aktiviert');
    } catch (err) {
      toast.error('Fehler beim Aktivieren');
    }
  };

  const handleSave = async (updates) => {
    if (readOnly) return;
    setSaving(true);
    try {
      const result = await whatsappApi.updateSettings({ ...updates });
      setSettings(prev => ({ ...prev, ...result }));
      toast.success('Gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleConnect = async () => {
    if (!connectForm.phoneNumberId) {
      toast.error('Phone Number ID erforderlich');
      return;
    }
    try {
      toast.loading('Verbindung wird hergestellt...');
      const result = await whatsappApi.connectMeta(connectForm.phoneNumberId, connectForm.businessAccountId, connectForm.phoneNumber);
      setSettings(prev => ({ ...prev, ...result }));
      toast.dismiss();
      toast.success('Meta Cloud API verbunden!');
      loadSettings();
    } catch (err) {
      toast.dismiss();
      toast.error(err.response?.data?.error || 'Verbindung fehlgeschlagen');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!settings) return null;

  const isConnected = settings.senderStatus === 'ONLINE';

  return (
    <div className="space-y-4">
      {/* Sub-Tabs */}
      <div className="flex overflow-x-auto gap-1 bg-gray-100 rounded-lg p-1">
        {SUB_TABS.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition ${
                activeTab === tab.id ? 'bg-white text-primary shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Status Tab */}
      {activeTab === 'status' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">WhatsApp-Agent Status</h3>
            {settings.metaPhoneNumberId && (
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-sm text-text-muted">{settings.enabled ? 'Aktiv' : 'Inaktiv'}</span>
                <div
                  onClick={!readOnly ? () => handleSave({ enabled: !settings.enabled }) : undefined}
                  className={`relative w-11 h-6 rounded-full transition cursor-pointer ${settings.enabled ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.enabled ? 'translate-x-5' : ''}`} />
                </div>
              </label>
            )}
          </div>

          {/* Connection Status */}
          {settings.metaPhoneNumberId ? (
            <>
              <div className={`p-4 rounded-lg border ${isConnected ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                <div className="flex items-center gap-3">
                  {isConnected ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-gray-400" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm font-medium">{isConnected ? 'Verbunden' : 'Nicht verbunden'}</p>
                    {settings.phoneNumber && (
                      <p className="text-xs text-text-muted">
                        {isEmbed ? '•••• ••• ••••' : settings.phoneNumber}
                      </p>
                    )}
                    {settings.metaDisplayName && !isEmbed && <p className="text-xs text-text-muted">{settings.metaDisplayName}</p>}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        const data = await whatsappApi.getSenderStatus();
                        setSettings(prev => ({ ...prev, ...data }));
                        toast.success('Status aktualisiert');
                      } catch (err) {
                        toast.error('Status-Abfrage fehlgeschlagen');
                      }
                    }}
                    className="p-1.5 text-gray-400 hover:text-primary rounded transition"
                    title="Status aktualisieren"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Trennen-Button (im Demo-Embed deaktiviert) */}
              {!readOnly && !isEmbed && (
                <button
                  onClick={async () => {
                    if (!confirm('WhatsApp-Verbindung wirklich trennen? Der Agent kann dann keine Nachrichten mehr empfangen oder senden.')) return;
                    setDisconnecting(true);
                    try {
                      await whatsappApi.deleteSender();
                      setSettings(prev => ({
                        ...prev,
                        metaPhoneNumberId: null,
                        metaBusinessAccountId: null,
                        metaDisplayName: null,
                        metaQualityRating: null,
                        phoneNumber: null,
                        senderStatus: null,
                        connectedAt: null,
                        enabled: false
                      }));
                      toast.success('WhatsApp-Verbindung getrennt');
                    } catch (err) {
                      toast.error(err.response?.data?.error || 'Trennen fehlgeschlagen');
                    } finally {
                      setDisconnecting(false);
                    }
                  }}
                  disabled={disconnecting}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition disabled:opacity-50 text-sm"
                >
                  {disconnecting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Wird getrennt...</>
                  ) : (
                    <><Unlink className="w-4 h-4" /> WhatsApp-Verbindung trennen</>
                  )}
                </button>
              )}
            </>
          ) : (
            <>
              {/* Nicht verbunden - Anleitung + Connect */}
              <div className="p-4 rounded-lg border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <XCircle className="w-5 h-5 text-gray-400" />
                  <p className="text-sm font-medium">Nicht verbunden</p>
                </div>

                {/* Schritt-für-Schritt Anleitung */}
                <div className="mb-4 space-y-2.5">
                  <p className="text-xs font-medium text-text-muted uppercase tracking-wide">Anleitung</p>
                  {[
                    { nr: '1', text: 'Meta Business-Konto erstellen (falls nicht vorhanden)', link: 'https://business.facebook.com/overview', linkText: 'Meta Business Suite' },
                    { nr: '2', text: 'WhatsApp-Konto hinzufügen & Nummer verifizieren', link: 'https://business.facebook.com/settings/whatsapp-business-accounts', linkText: 'WhatsApp-Konten' },
                    { nr: '3', text: 'Telefonnummer-ID kopieren (unter WhatsApp-Konto → Telefonnummern)' },
                    { nr: '4', text: 'ID unten einfügen und verbinden' },
                  ].map((step) => (
                    <div key={step.nr} className="flex gap-2 items-start">
                      <span className="w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">{step.nr}</span>
                      <div className="text-sm text-gray-700">
                        {step.text}
                        {step.link && (
                          <a href={step.link} target="_blank" rel="noopener noreferrer" className="ml-1 text-primary hover:underline inline-flex items-center gap-0.5">
                            {step.linkText} <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Connect Form */}
                {!readOnly && (
                  <div className="space-y-2 pt-3 border-t border-gray-100">
                    <input type="text" placeholder="Phone Number ID *" value={connectForm.phoneNumberId} onChange={(e) => setConnectForm(prev => ({ ...prev, phoneNumberId: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    <input type="text" placeholder="Business Account ID (optional)" value={connectForm.businessAccountId} onChange={(e) => setConnectForm(prev => ({ ...prev, businessAccountId: e.target.value }))} className="w-full px-3 py-2 border border-border rounded-lg text-sm" />
                    <button onClick={handleConnect} disabled={!connectForm.phoneNumberId} className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50">
                      Verbinden
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Einstellungen Tab */}
      {activeTab === 'einstellungen' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <h3 className="text-lg font-semibold">WhatsApp-spezifische Einstellungen</h3>
          <p className="text-sm text-text-muted">
            Autonomiestufe, Pflichtfelder und Preisauskunft werden zentral unter dem Tab "Einstellungen" konfiguriert.
          </p>

          <div>
            <label className="text-sm font-medium text-text block mb-1">Begrüßungstext</label>
            <textarea
              value={settings.greetingText || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, greetingText: e.target.value }))}
              onBlur={() => handleSave({ greetingText: settings.greetingText })}
              disabled={readOnly}
              rows={3}
              placeholder="Willkommen bei unserer WhatsApp-Beratung..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-text block mb-1">Nachricht außerhalb der Geschäftszeiten</label>
            <textarea
              value={settings.outOfHoursMessage || ''}
              onChange={(e) => setSettings(prev => ({ ...prev, outOfHoursMessage: e.target.value }))}
              onBlur={() => handleSave({ outOfHoursMessage: settings.outOfHoursMessage })}
              disabled={readOnly}
              rows={2}
              placeholder="Vielen Dank für Ihre Nachricht. Wir melden uns..."
              className="w-full px-3 py-2 border border-border rounded-lg text-sm resize-y"
            />
          </div>
        </div>
      )}

      {/* Konversationen Tab */}
      {activeTab === 'konversationen' && (
        <div className="bg-surface rounded-card shadow-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Konversationen</h3>
            <button onClick={loadConversations} className="p-2 text-text-muted hover:text-text">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Suchleiste */}
          <form onSubmit={handleConvSearch} className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Suche nach Name, Telefonnummer..."
                value={convSearchInput}
                onChange={(e) => setConvSearchInput(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg text-sm"
              />
            </div>
            <button type="submit" className="px-3 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90">
              Suchen
            </button>
            {convSearch && (
              <button
                type="button"
                onClick={() => { setConvSearchInput(''); setConvSearch(''); setConvPage(1); }}
                className="px-3 py-2 border border-border rounded-lg text-sm text-text-muted hover:bg-gray-50"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </form>

          {conversations.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-8">Keine Konversationen vorhanden</p>
          ) : (
            <div className="space-y-2">
              {conversations.map((conv) => (
                <div key={conv.id}>
                  <div
                    className={`p-3 rounded-lg border transition cursor-pointer ${
                      selectedConv?.id === conv.id ? 'border-primary bg-primary/5' : 'border-border hover:bg-gray-50'
                    }`}
                    onClick={() => loadConversationDetail(conv)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 rounded-full bg-green-100">
                          <MessageCircle className="w-3.5 h-3.5 text-green-600" />
                        </div>
                        <div>
                          {conv.kunde?.id && !isEmbed ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); navigate(`/kunden/${conv.kunde.id}`); }}
                              className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                              title="Zum Kunden"
                            >
                              {conv.kunde.name || conv.phoneNumber}
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          ) : (
                            <p className="text-sm font-medium">{conv.kunde?.name ? censorName(conv.kunde.name, isEmbed) : censorPhone(conv.phoneNumber, isEmbed)}</p>
                          )}
                          <p className="text-xs text-text-muted">{censorPhone(conv.phoneNumber, isEmbed)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-text-muted">{new Date(conv.updatedAt).toLocaleString('de-DE')}</p>
                        {conv.sessionActive && (
                          <span className="text-xs text-green-600 bg-green-50 px-1.5 py-0.5 rounded">Aktiv</span>
                        )}
                      </div>
                    </div>
                    {conv.nachrichten?.[0] && !selectedConv?.id === conv.id && (
                      <p className="text-xs text-text-muted mt-2 truncate">{censorBody(conv.nachrichten[0].inhalt, isEmbed)}</p>
                    )}
                  </div>

                  {/* Expanded Messages */}
                  {selectedConv?.id === conv.id && (
                    <div className="mt-1 rounded-lg border border-primary/20 bg-white">
                      {convLoading ? (
                        <div className="flex justify-center py-4">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                        </div>
                      ) : (
                        <>
                          {/* Header with session status */}
                          <div className="flex items-center justify-between p-3 border-b border-border">
                            <div className="flex items-center gap-2">
                              {conv.kunde?.id && !isEmbed ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/kunden/${conv.kunde.id}`)}
                                  className="text-sm font-medium text-primary hover:underline inline-flex items-center gap-1"
                                  title="Zum Kunden"
                                >
                                  {conv.kunde.name || conv.phoneNumber}
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                              ) : (
                                <p className="text-sm font-medium">{conv.kunde?.name ? censorName(conv.kunde.name, isEmbed) : censorPhone(conv.phoneNumber, isEmbed)}</p>
                              )}
                              {selectedConvDetail?.sessionActive && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs">24h-Fenster aktiv</span>
                              )}
                            </div>
                          </div>

                          {/* Handoff/Resume Bar */}
                          <div className="flex items-center justify-between p-2 mx-3 mt-2 rounded-lg border border-border">
                            {selectedConvDetail?.handoffActive ? (
                              <div className="flex items-center gap-2">
                                <UserCheck className="w-4 h-4 text-amber-600" />
                                <span className="text-sm text-amber-700 font-medium">Manueller Modus</span>
                                <span className="text-xs text-text-muted">
                                  — KI pausiert
                                  {selectedConvDetail.handoffAutoResumeAt && (
                                    <>, Auto-Resume: {new Date(selectedConvDetail.handoffAutoResumeAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</>
                                  )}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Bot className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-green-700 font-medium">KI-Agent aktiv</span>
                              </div>
                            )}
                            {!readOnly && (
                              <div className="flex items-center gap-2">
                                {selectedConvDetail?.handoffActive ? (
                                  <button onClick={handleResume} className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition flex items-center gap-1">
                                    <Bot className="w-3 h-3" />
                                    KI aktivieren
                                  </button>
                                ) : (
                                  <button onClick={handleHandoff} className="px-3 py-1 text-xs bg-amber-100 text-amber-700 rounded-full hover:bg-amber-200 transition flex items-center gap-1">
                                    <UserCheck className="w-3 h-3" />
                                    Übernehmen
                                  </button>
                                )}
                                <button
                                  onClick={() => handleToggleWaSperre(selectedConvDetail?.phoneNumber)}
                                  className={`px-3 py-1 text-xs rounded-full transition flex items-center gap-1 ${
                                    waSperren.some(s => s.kontakt === selectedConvDetail?.phoneNumber)
                                      ? 'bg-primary/10 text-primary hover:bg-primary/20'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  <VolumeX className="w-3 h-3" />
                                  {waSperren.some(s => s.kontakt === selectedConvDetail?.phoneNumber) ? 'Stumm' : 'Stumm schalten'}
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Messages */}
                          <div className="p-3 max-h-80 overflow-y-auto">
                            {selectedConvMessages.length === 0 ? (
                              <p className="text-sm text-text-muted text-center py-4">Keine Nachrichten</p>
                            ) : (
                              <div className="space-y-2">
                                {selectedConvMessages.map((msg, idx) => {
                                  const isIncoming = msg.richtung === 'eingehend' || msg.direction === 'inbound';
                                  const toolCalls = msg.toolCalls && Array.isArray(msg.toolCalls) ? msg.toolCalls : [];
                                  return (
                                    <div key={msg.id || idx} className={`flex ${isIncoming ? 'justify-start' : 'justify-end'}`}>
                                      <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                                        isIncoming
                                          ? 'bg-gray-100 text-text'
                                          : 'bg-green-100 text-green-900'
                                      }`}>
                                        {/* Inline-Medien (Bilder / Dokumente) */}
                                        {msg.mediaUrls && Array.isArray(msg.mediaUrls) && msg.mediaUrls.length > 0 && msg.mediaUrls[0]?.chatImageId && (
                                          <div className="mb-1.5 flex flex-wrap gap-1.5">
                                            {msg.mediaUrls.map((media, mIdx) => {
                                              if (!media.chatImageId) return null;
                                              const isImage = media.mimeType?.startsWith('image/');
                                              return isImage ? (
                                                <a key={mIdx} href={`/api/chat/images/${media.chatImageId}`} target="_blank" rel="noopener noreferrer">
                                                  <img
                                                    src={`/api/chat/images/${media.chatImageId}`}
                                                    alt={media.dateiname || 'WhatsApp-Bild'}
                                                    className="max-w-[200px] max-h-[150px] rounded object-cover cursor-pointer hover:opacity-90"
                                                  />
                                                </a>
                                              ) : (
                                                <a key={mIdx} href={`/api/chat/images/${media.chatImageId}`} target="_blank" rel="noopener noreferrer"
                                                   className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded ${isIncoming ? 'bg-gray-200 text-text hover:bg-gray-300' : 'bg-green-200 text-green-900 hover:bg-green-300'}`}>
                                                  <FileText size={14} />
                                                  <span className="truncate max-w-[150px]">{media.dateiname || 'Dokument'}</span>
                                                </a>
                                              );
                                            })}
                                          </div>
                                        )}
                                        <p className="whitespace-pre-wrap break-words">{censorBody(msg.inhalt || msg.content || msg.body, isEmbed)}</p>
                                        {toolCalls.length > 0 && (
                                          <details className="mt-1.5 border-t border-green-200 pt-1">
                                            <summary className="text-[10px] text-green-700 cursor-pointer flex items-center gap-1">
                                              <Bot size={10} /> {toolCalls.length} Tool-Call{toolCalls.length > 1 ? 's' : ''}
                                            </summary>
                                            <div className="mt-1 space-y-1">
                                              {toolCalls.map((tc, tIdx) => {
                                                let toolName = tc.name || '';
                                                let success = null;
                                                let resultPreview = '';
                                                try {
                                                  const content = typeof tc.content === 'string' ? JSON.parse(tc.content) : tc.content;
                                                  success = content?.success;
                                                  resultPreview = content?.message || content?.error || JSON.stringify(content).substring(0, 120);
                                                } catch { resultPreview = String(tc.content || '').substring(0, 120); }
                                                return (
                                                  <div key={tIdx} className="text-[10px] bg-green-50 rounded px-1.5 py-0.5 text-green-800">
                                                    {toolName && <span className="font-semibold">{toolName}: </span>}
                                                    {success === true && <span className="text-green-600">OK</span>}
                                                    {success === false && <span className="text-red-600">FEHLER</span>}
                                                    {' '}<span className="font-mono opacity-80">{resultPreview}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </details>
                                        )}
                                        <p className="text-[10px] opacity-60 mt-1">
                                          {new Date(msg.erstelltAm || msg.createdAt || msg.timestamp).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                                          {msg.messageStatus && ` · ${msg.messageStatus}`}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                                <div ref={messagesEndRef} />
                              </div>
                            )}
                          </div>

                          {/* Message Input */}
                          {!readOnly && (
                            <div className="p-3 border-t border-border flex gap-2">
                              <input
                                type="text"
                                value={manualMessage}
                                onChange={(e) => setManualMessage(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                                placeholder={selectedConvDetail?.sessionActive ? 'Nachricht schreiben...' : '24h-Fenster abgelaufen'}
                                disabled={sending || !selectedConvDetail?.sessionActive}
                                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-400"
                              />
                              <button
                                onClick={handleSendMessage}
                                disabled={!manualMessage.trim() || sending || !selectedConvDetail?.sessionActive}
                                className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50 flex items-center gap-1.5"
                              >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </button>
                            </div>
                          )}
                          {!selectedConvDetail?.sessionActive && (
                            <p className="text-[11px] text-amber-600 px-3 pb-3">
                              Das 24h-Fenster ist abgelaufen. Der Kunde muss zuerst eine neue Nachricht senden.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {convTotal > 20 && (
                <div className="flex items-center justify-center gap-2 pt-3">
                  <button onClick={() => setConvPage(p => Math.max(1, p - 1))} disabled={convPage <= 1} className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50">Zurück</button>
                  <span className="text-sm text-text-muted">Seite {convPage}</span>
                  <button onClick={() => setConvPage(p => p + 1)} disabled={conversations.length < 20} className="px-3 py-1 text-sm border border-border rounded disabled:opacity-50">Weiter</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Business App Tab */}
      {activeTab === 'business' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <h3 className="text-lg font-semibold">WhatsApp Business App Koexistenz</h3>
          <p className="text-sm text-text-muted">
            Wenn aktiviert, können Sie die WhatsApp Business App parallel zum KI-Agent nutzen.
            Nachrichten, die über die Business App gesendet werden, pausieren den KI-Agent temporär.
          </p>
          <div className="p-4 rounded-lg border border-border bg-yellow-50">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700">Hinweis</span>
            </div>
            <p className="text-xs text-yellow-600">
              Wenn die Business App aktiv ist und manuell antwortet, wird der KI-Agent für diese Konversation
              automatisch pausiert. Der KI-Agent übernimmt wieder nach Ablauf der konfigurierten Pausendauer.
            </p>
          </div>
        </div>
      )}

      {/* Antwort-Sperre Tab */}
      {activeTab === 'antwortsperre' && (
        <div className="bg-surface rounded-card shadow-card p-5 space-y-4">
          <div className="flex items-center gap-2">
            <VolumeX className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold">Auto-Antwort-Sperre (WhatsApp)</h3>
          </div>
          <p className="text-sm text-text-muted">
            Nachrichten von diesen Nummern werden weiterhin verarbeitet und dokumentiert, aber der KI-Agent sendet <strong>keine automatische Antwort</strong>. Die KI-generierte Antwort wird als "nicht gesendet" in der Konversation gespeichert.
          </p>

          {!readOnly && (
            <div className="flex items-center gap-2">
              <input
                type="tel"
                placeholder="+49 171 1234567"
                value={newWaSperrePhone}
                onChange={(e) => setNewWaSperrePhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (async () => {
                  if (!newWaSperrePhone.trim()) return;
                  try {
                    await whatsappApi.addAutoAntwortSperre(newWaSperrePhone.trim());
                    setNewWaSperrePhone('');
                    toast.success('Auto-Antwort-Sperre aktiviert');
                    loadWaSperren();
                  } catch (err) { toast.error('Fehler'); }
                })()}
                className="flex-1 px-3 py-2 border border-border rounded-lg text-sm"
              />
              <button
                onClick={async () => {
                  if (!newWaSperrePhone.trim()) return;
                  try {
                    await whatsappApi.addAutoAntwortSperre(newWaSperrePhone.trim());
                    setNewWaSperrePhone('');
                    toast.success('Auto-Antwort-Sperre aktiviert');
                    loadWaSperren();
                  } catch (err) { toast.error('Fehler'); }
                }}
                className="px-4 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
              >
                Sperre aktivieren
              </button>
            </div>
          )}

          <div className="space-y-2">
            {waSperren.map((item, i) => (
              <div key={item.id || i} className="flex items-center justify-between p-3 rounded-lg border border-border bg-primary/5">
                <div className="flex items-center gap-3">
                  <VolumeX className="w-4 h-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium">{item.kontakt}</p>
                    <p className="text-xs text-text-muted">
                      Gesperrt seit {new Date(item.erstelltAm).toLocaleDateString('de-DE')} · {item.grund}
                    </p>
                  </div>
                </div>
                {!readOnly && (
                  <button
                    onClick={() => handleToggleWaSperre(item.kontakt)}
                    className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
                  >
                    Antwort aktivieren
                  </button>
                )}
              </div>
            ))}
            {waSperren.length === 0 && (
              <p className="text-sm text-text-muted text-center py-4">Keine Sperren aktiv – alle Nachrichten werden automatisch beantwortet.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
